import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api/client";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { parseUtc } from "../utils/datetime";
import { Customer, Device } from "../types";
import { formatRelative } from "../utils/datetime";
import { useVisibleColumnCount, Breakpoint } from "../hooks/useVisibleColumns";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { usePublishScreenData } from "../utils/aiScreenContext";

type AutostartStatus = {
  windows_total: number;
  remediated: number;
  pending_command: number;
  needs_remediation: number;
};

/** The platform tag beside a hostname. Null platform == legacy Windows agent
 *  (predates the platform field).
 *
 *  TEXT, NOT AN EMOJI, and that is the whole point.
 *
 *  This used to return U+1FA9F (WINDOW) for Windows — Emoji 13.0, published
 *  2020. Windows Server 2019's Segoe UI Emoji has no glyph for it, so EVERY
 *  Windows row in the main device table rendered a tofu box: on the appliance
 *  itself, and on any technician's jump box of the same vintage. Windows is
 *  effectively the whole fleet, and this is the most-visited table in the
 *  product. Proved by rendering U+1FA9F next to the group-header icons, which
 *  do have glyphs — not inferred from the emoji version number.
 *
 *  The macOS case was worse: it returned an EMPTY STRING, so a Mac got no
 *  marker at all while its neighbours did.
 *
 *  A three-letter tag has no font dependency, and it is legible without
 *  hovering for the tooltip — which an icon never was.
 */
function platformTag(platform?: string | null): string {
  switch (platform) {
    case "macos":
      return "mac";
    case "linux":
      return "lnx";
    default:
      return "win";
  }
}

function platformLabel(platform?: string | null): string {
  switch (platform) {
    case "macos":
      return "macOS";
    case "linux":
      return "Linux";
    default:
      return "Windows";
  }
}

// Device grouping. We have no explicit device_type field, so classify from the
// OS string + platform: anything reporting a "Server" edition is a server;
// Windows client (10/11/8/7) and macOS are workstations; everything else
// (Linux, unknown, no OS yet) lands in "Other Devices".
type DeviceCategory = "server" | "workstation" | "other";

function deviceCategory(d: Device): DeviceCategory {
  const os = (d.os_version || "").toLowerCase();
  if (os.includes("server")) return "server";
  if (
    d.platform === "macos" ||
    /windows\s*(11|10|8|7|vista|xp)/.test(os) ||
    // legacy/null platform == Windows agent predating the platform field
    ((d.platform == null || d.platform === "windows") && os.includes("windows"))
  )
    return "workstation";
  return "other";
}

const CATEGORY_ORDER: DeviceCategory[] = ["server", "workstation", "other"];
// Each group gets a tasteful color accent so it reads clearly and looks
// intentional in BOTH light and dark mode. The dark: variants win over the
// global neutral overrides in index.css, so contrast stays high on the dark
// card surface (bg-white -> #1f2937).
const CATEGORY_META: Record<
  DeviceCategory,
  { label: string; glyph: string; bar: string; text: string; count: string }
> = {
  server: {
    label: "Servers",
    glyph: "🖥️",
    bar: "bg-indigo-100 dark:bg-indigo-500/20",
    text: "text-indigo-900 dark:text-indigo-100",
    count: "text-indigo-600 dark:text-indigo-300",
  },
  workstation: {
    label: "Workstations",
    glyph: "💻",
    bar: "bg-emerald-100 dark:bg-emerald-500/20",
    text: "text-emerald-900 dark:text-emerald-100",
    count: "text-emerald-600 dark:text-emerald-300",
  },
  other: {
    label: "Other Devices",
    glyph: "📦",
    bar: "bg-slate-200 dark:bg-slate-500/25",
    text: "text-slate-800 dark:text-slate-100",
    count: "text-slate-500 dark:text-slate-300",
  },
};

// Sortable columns. IPv4 sorts numerically (so .2 < .10); dates by timestamp;
// the boolean columns by truthiness; everything else case-insensitive string.
type SortKey =
  | "hostname"
  | "customer_name"
  | "ip_address"
  | "os_version"
  | "status"
  | "logged_in_user"
  | "agent_version"
  | "reboot"
  | "last_seen";

function ipKey(ip?: string | null): number {
  const m = (ip || "").match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return Number.MAX_SAFE_INTEGER; // non-IPv4 sorts last
  return ((+m[1] * 256 + +m[2]) * 256 + +m[3]) * 256 + +m[4];
}

function compareDevices(a: Device, b: Device, key: SortKey): number {
  switch (key) {
    case "ip_address":
      return ipKey(a.ip_address) - ipKey(b.ip_address);
    case "last_seen":
      return (a.last_seen ? Date.parse(a.last_seen) : 0) - (b.last_seen ? Date.parse(b.last_seen) : 0);
    case "reboot":
      return (a.pending_reboot ? 1 : 0) - (b.pending_reboot ? 1 : 0);
    default:
      return ((a[key] as string) || "")
        .toLowerCase()
        .localeCompare(((b[key] as string) || "").toLowerCase());
  }
}

// Monitored network devices (switches, printers, UPS, IoT, ICMP-only gear) —
// surfaced alongside agent endpoints so "All Devices" really means all.
type NetDev = {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  name: string;
  ip_address: string;
  kind: string;
  monitor_type?: string;
  last_status: string;
  last_polled_at?: string | null;
};

function NetDeviceRow({ d, glyph }: { d: NetDev; glyph: string }) {
  const online = d.last_status === "online";
  const unknown = d.last_status === "unknown" || !d.last_status;
  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
      <td className="px-3 py-3" />
      <td className="px-4 py-3 font-medium">
        <span className="mr-1.5 select-none" title={d.monitor_type === "icmp" ? "ICMP ping" : "SNMP"}>
          {glyph}
        </span>
        <Link to="/network" className="text-blue-600 dark:text-blue-400 hover:underline">
          {d.name}
        </Link>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        {d.customer_name ? (
          <span
            className="inline-block max-w-[11rem] truncate align-middle text-xs bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full"
            title={d.customer_name}
          >
            {d.customer_name}
          </span>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs hidden md:table-cell">{d.ip_address || "—"}</td>
      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs hidden lg:table-cell">
        {d.kind || "device"} · {d.monitor_type === "icmp" ? "ICMP" : "SNMP"}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${online ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : unknown ? "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${online ? "bg-green-500" : unknown ? "bg-gray-400" : "bg-red-500"}`}
          />
          {d.last_status || "unknown"}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-xs hidden lg:table-cell">—</td>
      <td className="px-4 py-3 text-gray-400 dark:text-gray-500 text-xs hidden lg:table-cell">—</td>
      <td className="px-4 py-3 hidden md:table-cell" />
      <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 hidden sm:table-cell">
        {d.last_polled_at
          ? formatRelative(d.last_polled_at)
          : "Never"}
      </td>
    </tr>
  );
}

export default function Devices() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [networkDevices, setNetworkDevices] = useState<NetDev[]>([]);
  const [netCollapsed, setNetCollapsed] = useState<Set<string>>(new Set());
  const toggleNet = (k: string) =>
    setNetCollapsed((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");

  // Bulk-selection state. Selected device IDs persist across filter
  // changes (so a user can search → check → search → check). The
  // action bar appears when the set is non-empty.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const [bulkCustomerId, setBulkCustomerId] = useState("");

  // One-time agent service auto-start remediation. Only surfaced to admins
  // (the bulk run endpoint is admin-only) and only when devices still need it.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [autostart, setAutostart] = useState<AutostartStatus | null>(null);
  const [remediateBusy, setRemediateBusy] = useState(false);
  const [remediateMsg, setRemediateMsg] = useState("");

  // Column sort + collapsible groups.
  // colSpan is an ATTRIBUTE, so a media query cannot vary it. The group-header
  // rows span the table, and with seven columns hidden a hardcoded colSpan={9}
  // was measured at 777px - making the table 814px wide on a 390px phone even
  // though the three visible headers total 297px. I had assumed browsers clamp
  // an over-large colSpan to the visible columns; they do not. This keeps the
  // span honest at every width. Order and breakpoints MUST mirror the <th>
  // list below.
  const DEVICE_COLUMNS: Breakpoint[] = [
    "base", // checkbox
    "base", // Hostname
    "sm",   // Customer
    "md",   // IP Address
    "lg",   // OS
    "base", // Status
    "lg",   // User
    "lg",   // Agent
    "md",   // Reboot
    "sm",   // Last Seen
  ];
  const visibleCols = useVisibleColumnCount(DEVICE_COLUMNS);
  // The group rows use one checkbox cell + one spanning cell.
  const groupSpan = Math.max(1, visibleCols - 1);
  const [sortKey, setSortKey] = useState<SortKey>("hostname");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [collapsed, setCollapsed] = useState<Set<DeviceCategory>>(new Set());

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(k);
      setSortDir("asc");
    }
  };
  const toggleCollapse = (c: DeviceCategory) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    });

  const { currentCustomerId } = useCustomer();

  // Scope the device list to the globally-selected customer (null = all).
  const load = useCallback(() => {
    const cfg =
      currentCustomerId != null ? { params: { customer_id: currentCustomerId } } : undefined;
    return Promise.all([
      api.get("/api/v1/devices", cfg).then((r) => setDevices(r.data)),
      api
        .get("/api/v1/network-devices", cfg)
        .then((r) => setNetworkDevices(r.data))
        .catch(() => setNetworkDevices([])),
    ]);
  }, [currentCustomerId]);

  const removeDuplicate = useCallback(
    async (id: number, hostname: string) => {
      if (
        !window.confirm(
          `Delete the stale duplicate record for ${hostname}? The current (newer/online) record ` +
            `is kept — only this outdated copy is removed. Cannot be undone.`,
        )
      )
        return;
      try {
        await api.delete(`/api/v1/devices/${id}`);
        await load();
      } catch {
        window.alert("The duplicate could not be deleted. Please try again.");
      }
    },
    [load],
  );

  useEffect(() => {
    load();
  }, [load]);
  // Refresh periodically so "last seen" / online status stay near-real-time
  // without a manual reload (the agent now stamps last_seen on every poll).
  // Via the hook so a forgotten background tab stops polling: this used to be
  // a bare setInterval that ran whether or not anyone was looking, and this
  // appliance has exhausted its DB connection pool twice in nine days.
  useAutoRefresh(load, 45000);

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data));
    loadAutostart();
  }, []);

  const loadAutostart = () =>
    api
      .get("/api/v1/devices/autostart-remediation/status")
      .then((r) => setAutostart(r.data))
      .catch(() => setAutostart(null));

  const runAutostartRemediation = async () => {
    setRemediateBusy(true);
    setRemediateMsg("");
    try {
      const r = await api.post("/api/v1/devices/autostart-remediation/run");
      setRemediateMsg(
        `Queued the auto-start fix on ${r.data.queued} device(s). ` +
          `It applies on each device's next check-in.`,
      );
      await loadAutostart();
    } catch (e: any) {
      setRemediateMsg(e.response?.data?.detail || "Remediation failed");
    } finally {
      setRemediateBusy(false);
    }
  };

  const filtered = useMemo(
    () =>
      devices.filter((d) => {
        const matchSearch =
          d.hostname.toLowerCase().includes(search.toLowerCase()) ||
          (d.ip_address || "").includes(search) ||
          (d.customer_name || "").toLowerCase().includes(search.toLowerCase());
        const matchStatus = statusFilter === "all" || d.status === statusFilter;
        return matchSearch && matchStatus;
      }),
    [devices, search, statusFilter],
  );

  // Same search/status filter applied to monitored network devices. Their
  // status is "online"/"offline"/"unknown" — map to the online/offline filter.
  const filteredNet = useMemo(
    () =>
      networkDevices
        .filter((d) => {
          const s = search.toLowerCase();
          const matchSearch =
            d.name.toLowerCase().includes(s) ||
            (d.ip_address || "").includes(search) ||
            (d.customer_name || "").toLowerCase().includes(s);
          const eff = d.last_status === "online" ? "online" : "offline";
          const matchStatus = statusFilter === "all" || statusFilter === eff;
          return matchSearch && matchStatus;
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [networkDevices, search, statusFilter],
  );

  // Sort the filtered list, then bucket into the three groups (preserving the
  // sorted order within each group).
  const grouped = useMemo(() => {
    const sorted = [...filtered].sort((a, b) => {
      const c = compareDevices(a, b, sortKey);
      return sortDir === "asc" ? c : -c;
    });
    const g: Record<DeviceCategory, Device[]> = { server: [], workstation: [], other: [] };
    sorted.forEach((d) => g[deviceCategory(d)].push(d));
    return g;
  }, [filtered, sortKey, sortDir]);

  // What the assistant can see: an identifier, the name, and the columns
  // people actually ask about. Published post-filter, because "these" means
  // the rows on screen and not everything loaded.
  usePublishScreenData(
    "All Devices",
    filtered.map((d: any) => ({
      id: d.id,
      hostname: d.hostname,
      customer: d.customer_name,
      status: d.status,
      os: d.os_version,
      agent: d.agent_version,
      logged_in: d.logged_in_user,
    })),
    search ? `search: ${search}` : undefined,
  );

  const filteredIds = filtered.map((d) => d.id);
  const allFilteredSelected =
    filtered.length > 0 && filteredIds.every((id) => selected.has(id));

  const toggleGroupSelected = (ids: number[], allSelected: boolean) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });

  const toggleAllFiltered = () => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggle = (id: number) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // Caret shown next to the active sort column; faint ↕ on the rest as an
  // affordance that the headers are clickable.
  const sortIndicator = (k: SortKey) =>
    sortKey === k ? (sortDir === "asc" ? "▲" : "▼") : "↕";

  const runBulkAssign = async () => {
    if (!bulkCustomerId || selected.size === 0) return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-action", {
        device_ids: Array.from(selected),
        action: "assign_customer",
        customer_id: Number(bulkCustomerId),
      });
      setBulkMsg(`Reassigned ${r.data.updated} device(s).`);
      clearSelection();
      setBulkCustomerId("");
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkMaintenance = async (enabled: boolean) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-action", {
        device_ids: Array.from(selected),
        action: enabled ? "maintenance_on" : "maintenance_off",
      });
      setBulkMsg(
        `${enabled ? "Entered" : "Exited"} maintenance mode on ${r.data.updated} device(s).`
      );
      clearSelection();
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkReinstall = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Reinstall the agent on ${selected.size} selected device(s)? ` +
          `Non-Windows devices are skipped. The reinstall runs when each device next checks in.`
      )
    )
      return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-reinstall-agent", {
        device_ids: Array.from(selected),
      });
      setBulkMsg(
        `Queued agent reinstall on ${r.data.queued} device(s)` +
          (r.data.skipped ? `; skipped ${r.data.skipped} (non-Windows / no access).` : ".")
      );
      clearSelection();
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Bulk reinstall failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkUninstall = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Uninstall the agent from ${selected.size} selected device(s)? ` +
          `Each agent removes its own service, Programs & Features entry, and install folder on ` +
          `its next check-in. Non-Windows and offline devices are skipped. The device records ` +
          `stay (use a device's Factory reset for full decommission).`
      )
    )
      return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-uninstall-agent", {
        device_ids: Array.from(selected),
        mode: "uninstall",
      });
      setBulkMsg(
        `Queued agent uninstall on ${r.data.queued} device(s)` +
          (r.data.skipped ? `; skipped ${r.data.skipped} (non-Windows / offline / no access).` : ".")
      );
      clearSelection();
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Bulk uninstall failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkClear = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Unassign ${selected.size} device(s) from their customers?`)) return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-action", {
        device_ids: Array.from(selected),
        action: "clear_customer",
      });
      setBulkMsg(`Unassigned ${r.data.updated} device(s).`);
      clearSelection();
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const duplicateIds = devices.filter((d) => d.stale_duplicate).map((d) => d.id);
  const cleanUpDuplicates = async () => {
    if (duplicateIds.length === 0) return;
    if (
      !window.confirm(
        `Delete ${duplicateIds.length} stale duplicate record(s)? The current (newer/online) ` +
          `record for each hostname is kept — only the outdated copies are removed. Cannot be undone.`,
      )
    )
      return;
    setBulkBusy(true);
    setBulkMsg("");
    try {
      const r = await api.post("/api/v1/devices/bulk-action", {
        device_ids: duplicateIds,
        action: "delete",
      });
      setBulkMsg(`Removed ${r.data.updated ?? duplicateIds.length} duplicate record(s).`);
      await load();
    } catch (e: any) {
      setBulkMsg(e.response?.data?.detail || "Duplicate cleanup failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">All Devices</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {filtered.length + filteredNet.length} of {devices.length + networkDevices.length} devices
          {selected.size > 0 && (
            <span className="ml-2 text-blue-700 dark:text-blue-300 font-medium">
              · {selected.size} selected
            </span>
          )}
        </span>
      </div>

      {isAdmin && duplicateIds.length > 0 && (
        <div className="mb-4 flex items-center justify-between gap-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          <span>
            ⚠ {duplicateIds.length} stale duplicate device record
            {duplicateIds.length === 1 ? "" : "s"} detected (re-enrollments superseded by a newer/
            online record for the same hostname).
          </span>
          <button
            onClick={cleanUpDuplicates}
            disabled={bulkBusy}
            className="shrink-0 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
            title="Delete all stale duplicate records (keeps the current record for each hostname)"
          >
            🗑 Clean up {duplicateIds.length} duplicate{duplicateIds.length === 1 ? "" : "s"}
          </button>
        </div>
      )}

      {isAdmin && autostart && autostart.needs_remediation > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[16rem]">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {autostart.needs_remediation} device(s) need the agent auto-start fix
            </p>
            <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
              Agents deployed before the installer change may have the
              AeroGaugeRMMAgent service set to Manual, so they don't restart after a
              reboot. Queue a one-time fix to set them to Automatic.
              {autostart.pending_command > 0 && (
                <> {autostart.pending_command} already queued, awaiting check-in.</>
              )}
            </p>
          </div>
          <button
            onClick={runAutostartRemediation}
            disabled={remediateBusy}
            className="bg-amber-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {remediateBusy ? "Queuing…" : "Remediate all"}
          </button>
        </div>
      )}
      {remediateMsg && (
        <div className="text-xs text-gray-600 dark:text-gray-400 mb-4">{remediateMsg}</div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap">
        <input
          type="text"
          placeholder="Search hostname, IP, customer..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none"
        >
          <option value="all">All Status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
            {selected.size} device(s) selected
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">Assign to customer:</label>
            <select
              value={bulkCustomerId}
              onChange={(e) => setBulkCustomerId(e.target.value)}
              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
            >
              <option value="">Select…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              onClick={runBulkAssign}
              disabled={!bulkCustomerId || bulkBusy}
              className="bg-blue-600 text-white text-xs px-3 py-1 rounded-lg disabled:opacity-50"
            >
              {bulkBusy ? "Working…" : "Apply"}
            </button>
          </div>
          <button
            onClick={runBulkClear}
            disabled={bulkBusy}
            className="text-xs border border-red-200 dark:border-red-700/50 text-red-700 dark:text-red-300 px-3 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
          >
            Unassign customer
          </button>
          <button
            onClick={() => runBulkMaintenance(true)}
            disabled={bulkBusy}
            className="text-xs bg-teal-600 text-white px-3 py-1 rounded-lg hover:bg-teal-700 disabled:opacity-50"
            title="Mute all alerting for the selected devices"
          >
            🔧 Enter maintenance
          </button>
          <button
            onClick={() => runBulkMaintenance(false)}
            disabled={bulkBusy}
            className="text-xs bg-amber-600 text-white px-3 py-1 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            title="Re-enable alerting for the selected devices"
          >
            Exit maintenance
          </button>
          <button
            onClick={runBulkReinstall}
            disabled={bulkBusy}
            className="text-xs bg-indigo-600 text-white px-3 py-1 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            title="Re-push the latest agent to the selected Windows devices (upgrades agents that can't self-update)"
          >
            ⟳ Reinstall agent
          </button>
          <button
            onClick={runBulkUninstall}
            disabled={bulkBusy}
            className="text-xs bg-rose-600 text-white px-3 py-1 rounded-lg hover:bg-rose-700 disabled:opacity-50"
            title="Uninstall the agent from the selected Windows devices (offboarding). Non-Windows/offline devices are skipped."
          >
            🗑 Uninstall agent
          </button>
          <button
            onClick={clearSelection}
            className="text-xs text-gray-600 dark:text-gray-400 hover:underline ml-auto"
          >
            Clear selection
          </button>
          {bulkMsg && (
            <span className="text-xs text-gray-700 dark:text-gray-300 w-full">{bulkMsg}</span>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  title={
                    allFilteredSelected
                      ? "Deselect all in view"
                      : "Select all in current filter"
                  }
                />
              </th>
              {/* The third element is the responsive visibility class, and it
                  MUST match the matching <td> below - a header and cell that
                  disagree shifts every column after it, which is worse than
                  scrolling. At 390px this leaves checkbox + Hostname + Status.
                  `hidden lg:table-cell` is a no-op at >=1024px, so the desktop
                  table is unchanged. */}
              {(
                [
                  ["hostname", "Hostname", ""],
                  ["customer_name", "Customer", "hidden sm:table-cell"],
                  ["ip_address", "IP Address", "hidden md:table-cell"],
                  ["os_version", "OS", "hidden lg:table-cell"],
                  ["status", "Status", ""],
                  ["logged_in_user", "User", "hidden lg:table-cell"],
                  ["agent_version", "Agent", "hidden lg:table-cell"],
                  ["reboot", "Reboot", "hidden md:table-cell"],
                  ["last_seen", "Last Seen", "hidden sm:table-cell"],
                ] as [SortKey, string, string][]
              ).map(([key, label, hide]) => (
                <th
                  key={key}
                  onClick={() => toggleSort(key)}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${hide}`}
                  title={`Sort by ${label}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {label}
                    <span
                      className={sortKey === key ? "text-blue-500" : "text-gray-300 dark:text-gray-600"}
                    >
                      {sortIndicator(key)}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {CATEGORY_ORDER.map((cat) => {
              const items = grouped[cat];
              if (items.length === 0) return null;
              const groupIds = items.map((gd) => gd.id);
              const allGroupSelected = groupIds.every((id) => selected.has(id));
              const isCollapsed = collapsed.has(cat);
              return (
                <React.Fragment key={cat}>
                  <tr className={`${CATEGORY_META[cat].bar} border-y border-gray-200 dark:border-gray-700`}>
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={allGroupSelected}
                        onChange={() => toggleGroupSelected(groupIds, allGroupSelected)}
                        title={`Select all ${CATEGORY_META[cat].label.toLowerCase()}`}
                      />
                    </td>
                    <td colSpan={groupSpan} className="px-4 py-2.5">
                      <button
                        onClick={() => toggleCollapse(cat)}
                        className={`inline-flex items-center gap-2 text-sm font-bold tracking-wide hover:opacity-80 ${CATEGORY_META[cat].text}`}
                        title={isCollapsed ? "Expand group" : "Collapse group"}
                      >
                        <span className="w-3 inline-block opacity-70">
                          {isCollapsed ? "▸" : "▾"}
                        </span>
                        <span>
                          {CATEGORY_META[cat].glyph} {CATEGORY_META[cat].label}
                        </span>
                        <span className={`text-xs font-semibold ${CATEGORY_META[cat].count}`}>
                          ({items.length})
                        </span>
                      </button>
                    </td>
                  </tr>
                  {!isCollapsed &&
                    items.map((d) => (
                      <tr
                key={d.id}
                className={
                  "hover:bg-gray-50 dark:hover:bg-gray-700/40 " + (selected.has(d.id) ? "bg-blue-50 dark:bg-blue-900/30" : "")
                }
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                </td>
                <td className="px-4 py-3 font-medium">
                  <span
                    className="mr-1.5 select-none align-middle text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500"
                    title={platformLabel(d.platform)}
                  >
                    {platformTag(d.platform)}
                  </span>
                  <Link to={`/devices/${d.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                    {d.hostname}
                  </Link>
                  {d.stale_duplicate &&
                    (isAdmin ? (
                      <button
                        onClick={() => removeDuplicate(d.id, d.hostname)}
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded hover:bg-amber-200 dark:hover:bg-amber-900/70"
                        title="Stale duplicate — a newer/online record exists for this hostname. Click to delete this duplicate."
                      >
                        dup <span aria-hidden>✕</span>
                      </button>
                    ) : (
                      <span
                        className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded"
                        title="Stale duplicate — a newer/online record exists for this hostname."
                      >
                        dup
                      </span>
                    ))}
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  {d.customer_name ? (
                    <span
                      className="inline-block max-w-[11rem] truncate align-middle text-xs bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full"
                      title={d.customer_name}
                    >
                      {d.customer_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 text-xs">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 font-mono text-xs hidden md:table-cell">{d.ip_address || "—"}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate text-xs hidden lg:table-cell">
                  {d.os_version || "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${d.status === "online" ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${d.status === "online" ? "bg-green-500" : "bg-red-500"}`}
                    />
                    {d.status}
                  </span>
                  {/* Only when alerting is ACTUALLY suppressed.
                   *
                   * This keyed off maintenance_mode, which outlives its own
                   * expiry: 63 devices carried the flag and 60 of those had
                   * expired three days earlier, so the console captioned
                   * sixty machines "alerting muted" while the evaluator was
                   * alerting on them normally. in_maintenance is the
                   * evaluator's own answer, so the two cannot disagree. */}
                  {d.in_maintenance && (
                    <span
                      className="ml-1.5 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 whitespace-nowrap"
                      title={
                        d.maintenance_until
                          ? `Alerting muted until ${parseUtc(d.maintenance_until)?.toLocaleString() ?? d.maintenance_until}`
                          : "Alerting muted with no end date — it stays muted until somebody turns it off"
                      }
                    >
                      🔧 {d.maintenance_until ? "muted" : "muted ∞"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300 max-w-[12rem] truncate hidden lg:table-cell" title={d.logged_in_user || ""}>
                  {d.logged_in_user || <span className="text-gray-400 dark:text-gray-500">—</span>}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                  {d.agent_version || "—"}
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  {d.pending_reboot && (
                    <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                      Needed
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 hidden sm:table-cell">
                  {d.last_seen
                    ? formatRelative(d.last_seen)
                    : "Never"}
                </td>
              </tr>
                    ))}
                </React.Fragment>
              );
            })}

            {(
              [
                {
                  key: "net",
                  label: "Network Devices",
                  glyph: "🌐",
                  items: filteredNet.filter((d) => d.kind !== "printer"),
                  head: "bg-cyan-100 dark:bg-cyan-500/20",
                  text: "text-cyan-900 dark:text-cyan-100",
                  count: "text-cyan-600 dark:text-cyan-300",
                },
                {
                  key: "printer",
                  label: "Printers",
                  glyph: "🖨️",
                  items: filteredNet.filter((d) => d.kind === "printer"),
                  head: "bg-orange-100 dark:bg-orange-500/20",
                  text: "text-orange-900 dark:text-orange-100",
                  count: "text-orange-600 dark:text-orange-300",
                },
              ] as const
            ).map((g) =>
              g.items.length === 0 ? null : (
                <React.Fragment key={g.key}>
                  <tr className={`${g.head} border-y border-gray-200 dark:border-gray-700`}>
                    <td className="px-3 py-2.5" />
                    <td colSpan={groupSpan} className="px-4 py-2.5">
                      <button
                        onClick={() => toggleNet(g.key)}
                        className={`inline-flex items-center gap-2 text-sm font-bold tracking-wide ${g.text} hover:opacity-80`}
                      >
                        <span className="w-3 inline-block opacity-70">
                          {netCollapsed.has(g.key) ? "▸" : "▾"}
                        </span>
                        <span>
                          {g.glyph} {g.label}
                        </span>
                        <span className={`text-xs font-semibold ${g.count}`}>({g.items.length})</span>
                      </button>
                    </td>
                  </tr>
                  {!netCollapsed.has(g.key) &&
                    g.items.map((d) => <NetDeviceRow key={`nd-${d.id}`} d={d} glyph={g.glyph} />)}
                </React.Fragment>
              )
            )}

            {filtered.length === 0 && filteredNet.length === 0 && (
              <tr>
                <td colSpan={visibleCols} className="px-4 py-12 text-center text-gray-400 dark:text-gray-500">
                  No devices found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
